import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import PressableScale from './motion/PressableScale';
import { View, Text, Platform, Animated, Dimensions, StyleSheet, TouchableOpacity, Image, Easing } from 'react-native';
import { BlurView } from 'expo-blur';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '../context/NavigationContext';
import { useTicker } from '../context/TickerContext';
import { useBackgroundQuery } from '../context/BackgroundQueryContext';
import { useAuth } from '../context/AuthContext';
import { getTeamLogo } from '../data/logos';
import { styles } from './gameTickerStyles';
import { TickerChrome, SkeletonTickerItem, TickerItem, getGameStatus, parseDate } from './gameTickerParts';

const { width: screenWidth } = Dimensions.get('window');

/**
 * GameTicker rAF loop keeps scroll in (-totalWidth, 0]. League navigation must align using
 * translateX = -leagueLeftPx + k*totalWidth and pick k for shortest path (old copyNum logic broke
 * when wrapped positions always had floor(abs(pos)/W) === 0).
 */
function wrapWebsiteTickerScroll(x, totalWidth) {
  if (!totalWidth || totalWidth <= 0) return x;
  let t = x;
  while (t <= -totalWidth) t += totalWidth;
  while (t > 0) t -= totalWidth;
  return t;
}

/** direction: 'next' = prefer more negative on distance ties; 'prev' = prefer less negative. */
function pickMarqueeLeagueScrollTarget(currentPos, leagueLeftPx, totalWidth, direction) {
  const W = totalWidth;
  const p = leagueLeftPx;
  const candidates = [-p, -p - W, -p + W];
  const EPS = 1;
  let best = candidates[0];
  let bestD = Math.abs(best - currentPos);
  for (const c of candidates) {
    const d = Math.abs(c - currentPos);
    if (d < bestD - EPS) {
      bestD = d;
      best = c;
    } else if (Math.abs(d - bestD) <= EPS) {
      if (direction === 'next' && c < best) best = c;
      if (direction === 'prev' && c > best) best = c;
    }
  }
  return best;
}

/** Web: full-width BlurView = expensive backdrop compositing. Native keeps frosted glass. */
const GameTicker = ({ fixtures = [], isVisible = true, onToggle, isLoading = false, isLoadingMore = false, isCollapsed = false, onCollapseToggle }) => {
  const { navigateTo, currentScreen, setRoutePayloadDirect } = useNavigation();
  const { tickerNameFormat, showTickerSGPIndicator, tickerSGPOnly, sgpCacheStatus, showLiveScores, liveScores, loadProgress, referenceTime } = useTicker();
  const { addBackgroundQuery, canAddBackgroundQuery } = useBackgroundQuery();
  const { userProfile } = useAuth();
  
  // Check if background query mode is enabled
  const backgroundQueryMode = userProfile?.preferences?.backgroundQueryMode === true;
  
  const scrollX = useRef(new Animated.Value(0)).current;
  /** 1 = scroll, 0 = pause on hover (ref avoids Animated + useNativeDriver:false hover tween every frame). */
  const hoverSpeedRef = useRef(1);
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(screenWidth);
  const animationRef = useRef(null);
  const lastScrollPosition = useRef(0);
  const [isPaused, setIsPaused] = useState(false);
  const [tickerSpeed, setTickerSpeed] = useState(1); // Display: 1x, 2x, 3x → Actual: 1x, 3x, 5x
  
  // Map display speed to actual multiplier
  const speedMultiplierMap = { 1: 1, 2: 3, 3: 5 };
  
  // ⭐ Scroll speed boost - user can scroll horizontally to speed up/slow down
  const scrollSpeedBoost = useRef(0); // Range: -1 (reverse/slow) to +3 (fast forward)
  const scrollDecayInterval = useRef(null);
  
  // Sort fixtures: Group by league, then by status (LIVE > SOON > UPCOMING)
  // Filter out finished games and SGP-only if enabled
  const sortedFixtures = useMemo(() => {
    let filtered = [...fixtures];
    
    // ⭐ Remove finished/ended games (use freeze-mode reference time when available)
    filtered = filtered.filter(f => {
      const status = getGameStatus(f.start_date, referenceTime).status;
      return status !== 'ended';
    });
    
    // ⭐ Filter to only show games with cached SGPs if SGP-only mode is enabled
    if (tickerSGPOnly && Object.keys(sgpCacheStatus).length > 0) {
      filtered = filtered.filter(f => {
        const fixtureId = f.fixture_id || f.id;
        return sgpCacheStatus[fixtureId] === true;
      });
    }

    // Define league priority order (major leagues first)
    const leaguePriority = {
      'nba': 1, 'nfl': 2, 'nhl': 3, 'mlb': 4,
      'ncaab': 5, 'ncaaf': 6, 'wnba': 7,
      'epl': 8, 'mls': 9, 'champions_league': 10,
      'ufc': 11, 'atp': 12, 'wta': 13,
    };
    
    return filtered.sort((a, b) => {
      const aLeague = (a.league || '').toLowerCase();
      const bLeague = (b.league || '').toLowerCase();
      
      // First: Sort by league (grouped together)
      const aLeaguePriority = leaguePriority[aLeague] || 99;
      const bLeaguePriority = leaguePriority[bLeague] || 99;
      
      if (aLeaguePriority !== bLeaguePriority) {
        return aLeaguePriority - bLeaguePriority;
      }
      
      // Same league: Sort by status (live > soon > upcoming)
      const statusOrder = { live: 0, soon: 1, upcoming: 2 };
      const aStatus = getGameStatus(a.start_date).status;
      const bStatus = getGameStatus(b.start_date).status;
      
      if (statusOrder[aStatus] !== statusOrder[bStatus]) {
        return (statusOrder[aStatus] ?? 3) - (statusOrder[bStatus] ?? 3);
      }
      
      // Same league and status: Sort by time
      const getTime = (f) => {
        const date = parseDate(f.start_date);
        return date ? date.getTime() : 0;
      };
      return getTime(a) - getTime(b);
    });
  }, [fixtures, tickerSGPOnly, sgpCacheStatus]);
  
  // Duplicate fixtures for seamless loop (two copies are enough; third copy doubled DOM cost)
  const displayFixtures = useMemo(() => {
    if (sortedFixtures.length === 0) return [];
    return [...sortedFixtures, ...sortedFixtures];
  }, [sortedFixtures]);

  const fixturesKey = useMemo(
    () => sortedFixtures.map((f) => f.fixture_id || f.id).join('|'),
    [sortedFixtures]
  );

  const [itemLayouts, setItemLayouts] = useState(() => []);

  useEffect(() => {
    setItemLayouts(Array(sortedFixtures.length).fill(null));
  }, [fixturesKey, sortedFixtures.length]);

  const handleRowLayout = useCallback((localIndex, event) => {
    const { x, width } = event.nativeEvent.layout;
    if (width <= 0) return;
    setItemLayouts((prev) => {
      if (prev.length !== sortedFixtures.length) return prev;
      const cur = prev[localIndex];
      if (cur && cur.x === x && cur.width === width) return prev;
      const next = [...prev];
      next[localIndex] = { x, width };
      return next;
    });
  }, [sortedFixtures.length]);

  // Measured row widths (first copy only); fallback to fixed estimates until onLayout completes
  const { totalWidth, leaguePositions } = useMemo(() => {
    const n = sortedFixtures.length;
    const EST_ITEM = 280;
    const EST_DIV = 80;

    if (n === 0) {
      return { totalWidth: 0, leaguePositions: [] };
    }

    const allMeasured =
      itemLayouts.length === n && itemLayouts.every((l) => l && l.width > 0);

    if (allMeasured) {
      const positions = [];
      const seen = new Set();
      let cumulative = 0;
      let contentExtent = 0;
      sortedFixtures.forEach((fixture, index) => {
        const layout = itemLayouts[index];
        const { x, width } = layout;
        contentExtent = Math.max(contentExtent, x + width);
        const league = (fixture.league || '').toUpperCase();
        const isFirstInLeague = league && !seen.has(league);
        if (isFirstInLeague) {
          seen.add(league);
          // Prefer flex-measured x when it matches summed widths (web can diverge slightly).
          const fromSum = cumulative;
          const fromLayout = x;
          const useLayout =
            Number.isFinite(fromLayout) &&
            Math.abs(fromLayout - fromSum) <= 12;
          positions.push({
            league,
            startIndex: index,
            position: useLayout ? fromLayout : fromSum,
          });
        }
        cumulative += width;
      });
      const totalW = Math.max(cumulative, contentExtent);
      return { totalWidth: totalW, leaguePositions: positions };
    }

    const positions = [];
    const seenLeagues = new Set();
    let currentPosition = 0;
    sortedFixtures.forEach((fixture, index) => {
      const league = (fixture.league || '').toUpperCase();
      const isFirstInLeague = !seenLeagues.has(league);

      if (league && isFirstInLeague) {
        seenLeagues.add(league);
        currentPosition += EST_DIV;
        positions.push({
          league,
          startIndex: index,
          position: currentPosition - EST_DIV,
        });
      }
      currentPosition += EST_ITEM;
    });

    return {
      totalWidth: currentPosition,
      leaguePositions: positions,
    };
  }, [sortedFixtures, itemLayouts]);
  
  // ═══════════════════════════════════════════════════════════════════════════
  // 🎯 REBUILT LEAGUE NAVIGATION SYSTEM - Simple & Reliable
  // ═══════════════════════════════════════════════════════════════════════════
  
  // Current league state
  const [currentLeagueIndex, setCurrentLeagueIndex] = useState(0);
  
  // Refs for navigation
  const isNavigatingRef = useRef(false);
  const wasNavigatingRef = useRef(false); // Track if we just finished navigating
  const navigationTargetRef = useRef(null);
  const lastDetectedLeagueRef = useRef(0);
  const lastTotalWidthRef = useRef(0);
  
  // Get current league info
  const currentLeague = leaguePositions[currentLeagueIndex] || null;
  
  // Get next and previous league info
  const nextLeagueIndex = leaguePositions.length > 1 ? (currentLeagueIndex + 1) % leaguePositions.length : -1;
  const prevLeagueIndex = leaguePositions.length > 1 ? (currentLeagueIndex - 1 + leaguePositions.length) % leaguePositions.length : -1;
  const nextLeague = nextLeagueIndex >= 0 ? leaguePositions[nextLeagueIndex] : null;
  const previousLeague = prevLeagueIndex >= 0 ? leaguePositions[prevLeagueIndex] : null;
  
  // ⭐ SIMPLE: Detect which league is at the left edge based on scroll position
  const detectLeagueAtPosition = useCallback((scrollPos) => {
    if (leaguePositions.length === 0 || totalWidth === 0) return 0;
    
    // scrollPos is negative (translateX moves content left)
    // Convert to positive offset within single copy (stable mod for floats)
    const absPos = Math.abs(scrollPos);
    let posInCopy = absPos % totalWidth;
    if (posInCopy < 0) posInCopy += totalWidth;
    if (posInCopy >= totalWidth) posInCopy %= totalWidth;
    
    // Find the last league whose position we've passed
    for (let i = leaguePositions.length - 1; i >= 0; i--) {
      if (posInCopy >= leaguePositions[i].position) {
        return i;
      }
    }
    return 0;
  }, [leaguePositions, totalWidth]);

  useEffect(() => {
    lastTotalWidthRef.current = 0;
  }, [fixturesKey]);

  // Keep scroll phase stable when totalWidth changes (estimate→measured or fixture set change)
  useEffect(() => {
    if (totalWidth <= 0) return;
    const prev = lastTotalWidthRef.current;
    lastTotalWidthRef.current = totalWidth;
    if (prev <= 0) return;
    const ratio = totalWidth / prev;
    if (Math.abs(ratio - 1) < 0.002) return;

    const pos = lastScrollPosition.current;
    const newPos = pos * ratio;
    lastScrollPosition.current = newPos;
    scrollX.setValue(newPos);
    const idx = detectLeagueAtPosition(newPos);
    lastDetectedLeagueRef.current = idx;
    setCurrentLeagueIndex(idx);
  }, [totalWidth, detectLeagueAtPosition, scrollX]);
  
  // ⭐ NAVIGATE TO SPECIFIC LEAGUE INDEX (shortest path on the looping strip + directional ties)
  const navigateToLeague = useCallback((targetLeagueIndex, direction = 'next') => {
    if (targetLeagueIndex < 0 || targetLeagueIndex >= leaguePositions.length) return;
    if (isNavigatingRef.current) return; // Prevent spam
    if (totalWidth === 0) return; // Not ready yet
    
    const targetLeague = leaguePositions[targetLeagueIndex];

    isNavigatingRef.current = true;

    const runNavigate = (stoppedValue) => {
      let currentPos = Number.isFinite(stoppedValue)
        ? stoppedValue
        : (lastScrollPosition.current || 0);
      try {
        if (!Number.isFinite(stoppedValue) && typeof scrollX.__getValue === 'function') {
          currentPos = scrollX.__getValue();
        }
      } catch (_) { /* keep ref */ }

      currentPos = wrapWebsiteTickerScroll(currentPos, totalWidth);
      lastScrollPosition.current = currentPos;
      scrollX.setValue(currentPos);

      const targetPos = pickMarqueeLeagueScrollTarget(
        currentPos,
        targetLeague.position,
        totalWidth,
        direction
      );

      lastDetectedLeagueRef.current = targetLeagueIndex;
      setCurrentLeagueIndex(targetLeagueIndex);

      // JS-driven only: native-driver timing leaves __getValue stale vs DOM → wrong jump deltas.
      Animated.timing(scrollX, {
        toValue: targetPos,
        duration: 450,
        easing: Easing.inOut(Easing.cubic),
        useNativeDriver: false,
      }).start(() => {
        const wrapped = wrapWebsiteTickerScroll(targetPos, totalWidth);
        lastScrollPosition.current = wrapped;
        scrollX.setValue(wrapped);
        isNavigatingRef.current = false;
      });
    };

    scrollX.stopAnimation(runNavigate);
  }, [leaguePositions, totalWidth, scrollX]);
  
  // ⭐ Skip to next league
  const skipToNextLeague = useCallback(() => {
    if (nextLeagueIndex < 0) return;
    navigateToLeague(nextLeagueIndex, 'next');
  }, [nextLeagueIndex, navigateToLeague]);
  
  // ⭐ Skip to previous league  
  const skipToPreviousLeague = useCallback(() => {
    if (prevLeagueIndex < 0) return;
    navigateToLeague(prevLeagueIndex, 'prev');
  }, [prevLeagueIndex, navigateToLeague]);
  
  // ⭐ Handle horizontal scroll to control ticker speed
  const handleWheel = useCallback((e) => {
    // Only respond to horizontal scroll (deltaX)
    if (Math.abs(e.deltaX) > Math.abs(e.deltaY) && Math.abs(e.deltaX) > 5) {
      e.preventDefault();
      
      // Scroll right (positive deltaX) = speed up
      // Scroll left (negative deltaX) = slow down
      const delta = e.deltaX * 0.02; // Sensitivity
      scrollSpeedBoost.current = Math.max(-0.8, Math.min(4, scrollSpeedBoost.current + delta));
      
      // Clear existing decay and start new one
      if (scrollDecayInterval.current) {
        clearInterval(scrollDecayInterval.current);
      }
      
      // Gradually decay boost back to 0
      scrollDecayInterval.current = setInterval(() => {
        if (Math.abs(scrollSpeedBoost.current) < 0.05) {
          scrollSpeedBoost.current = 0;
          clearInterval(scrollDecayInterval.current);
          scrollDecayInterval.current = null;
        } else {
          scrollSpeedBoost.current *= 0.92; // Decay towards 0
        }
      }, 50);
    }
  }, []);
  
  // Cleanup decay interval on unmount
  useEffect(() => {
    return () => {
      if (scrollDecayInterval.current) {
        clearInterval(scrollDecayInterval.current);
      }
    };
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // 🎬 REBUILT ANIMATION LOOP - Simple continuous scroll with league detection
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!isVisible || sortedFixtures.length === 0 || isPaused || totalWidth === 0) {
      return;
    }
    
    const baseSpeed = 40; // pixels per second (increased from 25 for better engagement)
    let startTime = Date.now();
    let currentPosition = lastScrollPosition.current || 0;
    
    let frameId;
    const animate = () => {
      // Skip if navigation animation is in progress
      if (isNavigatingRef.current) {
        // Mark that we're navigating so we can sync position when done
        wasNavigatingRef.current = true;
        frameId = requestAnimationFrame(animate);
        return;
      }
      
      // If we just finished navigating, sync position from the navigation result
      if (wasNavigatingRef.current) {
        currentPosition = lastScrollPosition.current;
        startTime = Date.now();
        wasNavigatingRef.current = false;
      }
      
      const now = Date.now();
      const deltaTime = (now - startTime) / 1000;
      startTime = now;
      
      const hoverSpeed = hoverSpeedRef.current;
      const actualSpeed = speedMultiplierMap[tickerSpeed] || 1;
      const combinedSpeed = actualSpeed * hoverSpeed * (1 + scrollSpeedBoost.current);
      
      // Move left (decrease position)
      currentPosition -= baseSpeed * combinedSpeed * deltaTime;
      
      // Two copies: when one full width has scrolled past, jump forward by one width
      if (currentPosition <= -totalWidth) {
        currentPosition += totalWidth;
      }
      
      // Update the animated value
      scrollX.setValue(currentPosition);
      lastScrollPosition.current = currentPosition;
      
      // ⭐ Detect current league and update state if changed (throttled to avoid spam)
      const detectedLeague = detectLeagueAtPosition(currentPosition);
      if (detectedLeague !== lastDetectedLeagueRef.current) {
        lastDetectedLeagueRef.current = detectedLeague;
        setCurrentLeagueIndex(detectedLeague);
      }
      
      frameId = requestAnimationFrame(animate);
    };
    
    frameId = requestAnimationFrame(animate);
    
    return () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
    };
  }, [isVisible, sortedFixtures.length, isPaused, totalWidth, tickerSpeed, detectLeagueAtPosition]);
  
  const handleGamePress = async (fixture) => {
    const fixtureId = fixture.fixture_id || fixture.id;
    const query = `${fixture.home_team} vs ${fixture.away_team}`;
    
    if (!fixtureId) {
      console.warn('[GameTicker] ⚠️ No fixtureId for ticker click:', { home: fixture.home_team, away: fixture.away_team, keys: Object.keys(fixture) });
    } else {
      console.warn('[GameTicker] 🎯 Ticker click fixtureId:', fixtureId, fixture.home_team, 'vs', fixture.away_team);
    }
    
    // ⭐ If SGP-only mode is enabled, use SGP search behavior
    if (tickerSGPOnly) {
      // Navigate to SgpFlow with specific fixture context and force SGP mode
      const payload = { 
        query,
        fixtureId,
        mode: 'sgp',
        fromCachedSGP: true, // Flag to indicate we're viewing cached SGPs
        fromTicker: true, // ⭐ Track that this query came from ticker click
        league: fixture.league,
      };
      
      if (currentScreen === 'SgpFlow') {
        try { 
          window.sessionStorage.setItem('sgp:lastQuery', query);
          window.sessionStorage.setItem('sgp:fixtureId', fixtureId);
          window.sessionStorage.setItem('sgp:fromCached', 'true');
        } catch {}
        if (setRoutePayloadDirect) {
          setRoutePayloadDirect(payload);
        }
      } else {
        navigateTo('SgpFlow', { payload });
      }
      return;
    }

    // ⭐ Regular game search behavior - NOW PASSES fixtureId for exact matchup filtering
    const payload = { 
      query,
      fixtureId, // Pass fixture ID for exact game matching
      league: fixture.league,
      sport: fixture.sport,
      fromTicker: true, // ⭐ Track that this query came from ticker click
    };
    
    // Check if background query mode is enabled
    if (backgroundQueryMode && canAddBackgroundQuery) {
      // Add to background queue - no page navigation
      await addBackgroundQuery(query, {
        league: fixture.league,
        sport: fixture.sport,
        fromTicker: true,
        fixtureId, // ⭐ CRITICAL: Pass fixtureId for exact matchup filtering!
      });
      return;
    }
    
    // If already on SgpFlow, directly update the payload to trigger a new query
    if (currentScreen === 'SgpFlow') {
      // Store query in session storage for the effect to pick up
      try { 
        window.sessionStorage.setItem('sgp:lastQuery', query); 
      } catch {}
      // Directly update route payload to trigger the useEffect
      if (setRoutePayloadDirect) {
        setRoutePayloadDirect(payload);
      }
    } else {
      navigateTo('SgpFlow', { payload });
    }
  };
  
  // ⭐ Handle SGP badge click - navigate directly to SGP view for this fixture
  const handleSGPPress = async (fixture) => {
    const fixtureId = fixture.fixture_id || fixture.id;
    const query = `${fixture.home_team} vs ${fixture.away_team} SGPs`;
    
    // Navigate to SgpFlow with specific fixture context and force SGP mode
    const payload = { 
      query,
      fixtureId,
      mode: 'sgp',
      fromCachedSGP: true,
      league: fixture.league,
      sport: fixture.sport,
    };
    
    // Respect background query mode — same as regular ticker clicks
    if (backgroundQueryMode && canAddBackgroundQuery) {
      await addBackgroundQuery(query, {
        league: fixture.league,
        sport: fixture.sport,
        fromTicker: true,
        fixtureId,
        mode: 'sgp',
        fromCachedSGP: true,
      });
      return;
    }

    // If already on SgpFlow, directly update the payload
    if (currentScreen === 'SgpFlow') {
      try { 
        window.sessionStorage.setItem('sgp:lastQuery', query);
        window.sessionStorage.setItem('sgp:fixtureId', fixtureId);
        window.sessionStorage.setItem('sgp:fromCached', 'true');
      } catch {}
      if (setRoutePayloadDirect) {
        setRoutePayloadDirect(payload);
      }
    } else {
      navigateTo('SgpFlow', { payload });
    }
  };

  // 🔴 Handle LIVE badge click - query live odds for this in-progress fixture
  const handleLivePress = async (fixture) => {
    const fixtureId = fixture.fixture_id || fixture.id;
    const query = `${fixture.home_team} vs ${fixture.away_team}`;

    const payload = {
      query,
      fixtureId,
      isLive: true,
      fromTicker: true,
      league: fixture.league,
      sport: fixture.sport,
    };

    // Respect background query mode — same as regular and SGP ticker clicks
    if (backgroundQueryMode && canAddBackgroundQuery) {
      await addBackgroundQuery(query, {
        league: fixture.league,
        sport: fixture.sport,
        fromTicker: true,
        fixtureId,
        isLive: true,
      });
      return;
    }

    if (currentScreen === 'SgpFlow') {
      try {
        window.sessionStorage.setItem('sgp:lastQuery', query);
        window.sessionStorage.setItem('sgp:fixtureId', fixtureId);
      } catch {}
      if (setRoutePayloadDirect) {
        setRoutePayloadDirect(payload);
      }
    } else {
      navigateTo('SgpFlow', { payload });
    }
  };
  
  const handlePauseToggle = () => {
    setIsPaused(!isPaused);
  };
  
  if (!isVisible) return null;
  
  // Show skeleton loading state
  if (isLoading && sortedFixtures.length === 0) {
    return (
      <View style={styles.container}>
        <TickerChrome>
          <View style={styles.gradientBg} />
          <View style={styles.controls}>
            <View style={[styles.controlBtn, { opacity: 0.5 }]}>
              <Ionicons name="pause" size={12} color="rgba(255,255,255,0.4)" />
            </View>
            {onCollapseToggle && (
              <PressableScale 
                onPress={onCollapseToggle}
                style={styles.controlBtn}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              >
                <Ionicons 
                  name="chevron-up" 
                  size={12} 
                  color="rgba(255,255,255,0.6)" 
                />
              </PressableScale>
            )}
          </View>
          <View style={styles.skeletonWrapper}>
            {/* Show 5 skeleton items */}
            {[0, 1, 2, 3, 4].map((i) => (
              <SkeletonTickerItem key={i} index={i} />
            ))}
          </View>
          {/* Edge fades with blur */}
          <View style={[styles.edgeFade, styles.leftFade]} pointerEvents="none">
            {Platform.OS !== 'web' && (
              <BlurView intensity={20} tint="dark" style={StyleSheet.absoluteFill} />
            )}
          </View>
          <View style={[styles.edgeFade, styles.rightFade]} pointerEvents="none">
            {Platform.OS !== 'web' && (
              <BlurView intensity={20} tint="dark" style={StyleSheet.absoluteFill} />
            )}
          </View>
        </TickerChrome>
      </View>
    );
  }
  
  // Show empty state only if not loading
  if (sortedFixtures.length === 0) {
    return (
      <View style={styles.container}>
        <TickerChrome>
          <View style={styles.controls}>
            {onCollapseToggle && (
              <PressableScale 
                onPress={onCollapseToggle}
                style={styles.controlBtn}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              >
                <Ionicons 
                  name="chevron-up" 
                  size={12} 
                  color="rgba(255,255,255,0.6)" 
                />
              </PressableScale>
            )}
          </View>
          <View style={styles.emptyState}>
            <Ionicons name="calendar-outline" size={16} color="rgba(255,255,255,0.4)" />
            <Text style={styles.emptyText}>No games available</Text>
          </View>
        </TickerChrome>
      </View>
    );
  }
  
  return (
    <View style={styles.container}>
      <TickerChrome>
        {/* Background gradient */}
        <View style={styles.gradientBg} />
        
        {/* Control buttons */}
        <View style={styles.controls}>
          {/* Loading more indicator - subtle pulse when fetching additional data */}
          {isLoadingMore && (
            <View style={styles.loadingMoreIndicator}>
              <Animated.View style={[styles.loadingDot, { opacity: 0.6 }]} />
            </View>
          )}
          
          {/* Speed toggle button - cycles through 1x → 2x → 3x → paused */}
          <PressableScale
            onPress={() => {
              if (isPaused) {
                // If paused, unpause and set to 1x
                setIsPaused(false);
                setTickerSpeed(1);
              } else if (tickerSpeed >= 3) {
                // After 3x, pause
                setIsPaused(true);
              } else {
                // Cycle through speeds
                setTickerSpeed(prev => prev + 1);
              }
            }}
            style={styles.speedToggleBtn}
            hitSlop={{ top: 10, bottom: 10, left: 5, right: 5 }}
          >
            {isPaused ? (
              <Ionicons name="pause" size={12} color="rgba(255,255,255,0.8)" />
            ) : (
              <Text style={styles.speedToggleText}>{tickerSpeed}x</Text>
            )}
          </PressableScale>
          
          {onCollapseToggle && (
            <PressableScale 
              onPress={onCollapseToggle}
              style={styles.controlBtn}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <Ionicons 
                name={isCollapsed ? "chevron-down" : "chevron-up"} 
                size={12} 
                color="rgba(255,255,255,0.6)" 
              />
            </PressableScale>
          )}
          
          {/* Previous League - back button */}
          {leaguePositions.length > 1 && previousLeague && (
            <PressableScale 
              onPress={skipToPreviousLeague}
              style={styles.prevLeagueBtn}
              hitSlop={{ top: 10, bottom: 10, left: 5, right: 5 }}
            >
              <Ionicons name="chevron-back" size={14} color="#c4b5fd" />
              <Text style={styles.prevLeagueLabel}>{previousLeague.league}</Text>
            </PressableScale>
          )}
        </View>
        
        {/* Scrolling content */}
        <View 
          style={styles.tickerWrapper}
          onLayout={(e) => setContainerWidth(e.nativeEvent.layout.width)}
          {...(Platform.OS === 'web' ? {
            onMouseEnter: () => { hoverSpeedRef.current = 0; },
            onMouseLeave: () => { hoverSpeedRef.current = 1; },
            onWheel: handleWheel,
          } : {})}
        >
          <Animated.View 
            style={[
              styles.tickerContent,
              { 
                transform: [{ translateX: scrollX }],
                width: totalWidth * 2,
              }
            ]}
          >
            {displayFixtures.map((fixture, index) => {
              const fixtureId = fixture.fixture_id || fixture.id;
              // Get live score data if available
              const liveScore = showLiveScores && liveScores ? liveScores[fixtureId] : null;
              
              // ⭐ Determine if first/last in league for section dividers
              const currentLeague = (fixture.league || '').toLowerCase();
              const prevFixture = displayFixtures[index - 1];
              const nextFixture = displayFixtures[index + 1];
              const prevLeague = prevFixture ? (prevFixture.league || '').toLowerCase() : '';
              const nextLeague = nextFixture ? (nextFixture.league || '').toLowerCase() : '';
              const isFirstInLeague = currentLeague !== prevLeague;
              const isLastInLeague = currentLeague !== nextLeague;
              
              return (
                <TickerItem 
                  key={`${fixtureId}-${index}`}
                  fixture={fixture}
                  index={index}
                  nameFormat={tickerNameFormat}
                  onPress={() => handleGamePress(fixture)}
                  onSGPPress={handleSGPPress}
                  onLivePress={handleLivePress}
                  showSGPIndicator={showTickerSGPIndicator}
                  hasCachedSGPs={sgpCacheStatus[fixtureId] === true}
                  liveScore={liveScore}
                  isFirstInLeague={isFirstInLeague}
                  isLastInLeague={isLastInLeague}
                  onRowLayout={index < sortedFixtures.length ? (e) => handleRowLayout(index, e) : undefined}
                  referenceTime={referenceTime}
                />
              );
            })}
          </Animated.View>
        </View>
        
        {/* Edge fades with blur */}
        <View style={[styles.edgeFade, styles.leftFade]} pointerEvents="none">
          {Platform.OS !== 'web' && (
            <BlurView intensity={20} tint="dark" style={StyleSheet.absoluteFill} />
          )}
        </View>
        <View style={[styles.edgeFade, styles.rightFade]} pointerEvents="none">
          {Platform.OS !== 'web' && (
            <BlurView intensity={20} tint="dark" style={StyleSheet.absoluteFill} />
          )}
        </View>
        
        {/* Right side controls - Next League button */}
        <View style={styles.controlsRight}>
          {leaguePositions.length > 1 && nextLeague ? (
            <PressableScale 
              onPress={skipToNextLeague}
              style={styles.nextLeagueBtn}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <Text style={styles.nextLeagueLabel}>{nextLeague.league}</Text>
              <Ionicons name="chevron-forward" size={14} color="#c4b5fd" />
            </PressableScale>
          ) : (
            <View style={styles.leagueIndicator}>
              <Text style={styles.currentLeagueText}>
                {leaguePositions[0]?.league || ''}
              </Text>
            </View>
          )}
        </View>
      </TickerChrome>
    </View>
  );
};


export default GameTicker;

